import { useState, useEffect } from 'react';
import { X, Calendar, Clock, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OneOnOneBookingModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [settings, setSettings] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAvailableSlots();
  }, [currentMonth]);

  const loadAvailableSlots = async () => {
    try {
      const startDate = new Date(currentMonth);
      startDate.setDate(1);
      const endDate = new Date(currentMonth);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);

      const response = await axios.get(`${API}/one-on-one/available-slots`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      setAvailableSlots(response.data.slots);
      setSettings(response.data.settings);
    } catch (error) {
      toast.error('Failed to load availability');
    }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getSlotsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  const changeMonth = (delta) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentMonth(newDate);
  };

  const handleSubmit = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setStep(3);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/one-on-one/bookings`, {
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
        booking_datetime: selectedSlot.datetime,
        notes: formData.notes,
        origin_url: originUrl
      });
      
      window.location.href = response.data.payment_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setLoading(false);
      setStep(2);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl border-4 border-[#E8B67A] p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl retro-title text-white">Book One-on-One Session</h2>
          <button onClick={onClose} className="text-white hover:text-[#E8B67A]">
            <X size={24} />
          </button>
        </div>

        {/* Step 1: Select Date & Time */}
        {step === 1 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-4">Select Date & Time</h3>
            
            {/* Month Navigation */}
            <div className="flex justify-between items-center mb-4">
              <button 
                onClick={() => changeMonth(-1)} 
                className="retro-button-small"
                disabled={currentMonth <= new Date()}
              >
                ← Previous
              </button>
              <h4 className="text-lg font-bold text-white">
                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h4>
              <button onClick={() => changeMonth(1)} className="retro-button-small">
                Next →
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 mb-6">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-bold text-white p-2 text-sm">
                  {day}
                </div>
              ))}
              {getDaysInMonth().map((day, index) => {
                const slots = getSlotsForDay(day);
                const hasAvailability = slots.length > 0;
                const isPast = day && new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < new Date();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[60px] p-1 border-2 rounded text-sm ${
                      day ? (hasAvailability && !isPast ? 'border-green-400 bg-green-900/30 cursor-pointer hover:bg-green-800/40' : 'border-[#E8B67A] bg-[#2C1810]/50') : 'bg-[#2C1810]/20 border-gray-600'
                    } ${isPast ? 'opacity-50' : ''}`}
                  >
                    {day && (
                      <>
                        <div className="font-bold text-white">{day}</div>
                        {hasAvailability && !isPast && (
                          <div className="text-xs text-green-300 mt-1">
                            {slots.length} slots
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Time Slots for Selected Date */}
            <div className="space-y-2">
              <h4 className="font-bold text-white mb-2">Available Time Slots:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-[200px] overflow-y-auto">
                {availableSlots.slice(0, 30).map((slot) => (
                  <button
                    key={slot.datetime}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setStep(2);
                    }}
                    className={`p-3 border-2 rounded text-sm ${
                      selectedSlot?.datetime === slot.datetime
                        ? 'border-[#E8B67A] bg-[#E8B67A] text-[#2C1810]'
                        : 'border-[#E8B67A] bg-[#2C1810]/50 text-white hover:bg-[#2C1810]/70'
                    }`}
                  >
                    <div className="font-bold">
                      {new Date(slot.datetime).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div>{slot.time}</div>
                  </button>
                ))}
              </div>
              {availableSlots.length === 0 && (
                <p className="text-center text-white py-4">
                  No available slots in the next 30 days. Try selecting a different month.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Contact Information */}
        {step === 2 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-4">Your Information</h3>
            
            {selectedSlot && (
              <div className="bg-[#E8B67A]/20 p-4 mb-6 rounded-lg border-2 border-[#E8B67A]">
                <div className="flex items-center space-x-2 text-white">
                  <Calendar className="w-5 h-5" />
                  <span className="font-bold">
                    {new Date(selectedSlot.datetime).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-white mt-2">
                  <Clock className="w-5 h-5" />
                  <span>{selectedSlot.time} ({selectedSlot.duration_minutes} minutes)</span>
                </div>
                <div className="flex items-center space-x-2 text-white mt-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-bold">$100.00</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-white font-bold mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-bold mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-bold mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-white font-bold mb-2">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400"
                  rows="3"
                  placeholder="Any specific concerns or topics you'd like to discuss..."
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button 
                onClick={() => setStep(1)} 
                className="retro-button bg-gray-400"
              >
                Back
              </button>
              <button 
                onClick={handleSubmit} 
                disabled={loading}
                className="retro-button flex-1"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#2C1810] text-xl">Redirecting to payment...</p>
          </div>
        )}
      </div>
    </div>
  );
}
